import React from 'react';
import { ICustomHeader, gridControlType } from 'app/models/Grid';
import NoteIcon from 'app/assets/images/Notes.png';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CredentialingLookup } from 'app/models/Credentialing/CredentialingLookups';
import HistoryIcon from '@mui/icons-material/History';
import {
  getDate,
  getCredentialName,
  getDateNoFormat,
  getDateText,
  getCredentialNameNoFormat,
  detailColumnsAndRows,
  getMetadataForReqID,
  getCredentialNameBold,
} from '../tableHelpers';
import { ICredentials } from 'app/models/Credentialing/Credentials';
import { AttachmentAction, CredentialAttachmentToolTip } from '../../Attachments/AttachmentHoverView';
import { groupBy, nestedChildren } from 'app/helpers/arrayHelpers';
import VersionDetailsHoverView from '../VersionDetailsHoverView';
import { childSorting } from '../HumanResources/HumanResourcesGroupingHelper';

const getMainTableColumns = (
  sortable: boolean = true,
  viewingDuplicates: boolean = false,
  historical: boolean = false,
) => {
  const headers: ICustomHeader[] = [
    {
      title: 'credentialName',
      value: 'Credential Name',
      sortable: sortable,
      sortByField: 'credentialNameNoFormat',
    },
    {
      title: 'dateCompleted',
      value: 'Date Completed',
      sortable: sortable,
      sortByField: 'dateCompletedNoFormat',
      defaultSort: !viewingDuplicates,
      control: {
        type: gridControlType.date,
      },
    },
    {
      title: 'lastUpdate',
      value: 'Updated Date',
      sortable: sortable,
      defaultSort: viewingDuplicates,
      sortByField: 'lastUpdateNoFormat',
      control: {
        type: gridControlType.label,
      },
    },
    {
      title: 'versioning',
      value: 'Versions',
      icon: true,
      versioning: !historical,
    },
    {
      title: 'documentIcon',

      value: 'Document',
      // clickable: true,
      navigationIds: [''],
      navigationExp: '/',
      hasHoverView: true,
      customHoverView: true,
    },
  ];
  return headers;
};

export const groupedAssessments = list => {
  return groupBy<keyof ICredentials>(
    list,
    ['credentialName'],
    [
      { item: 'dateCompleted', type: 'shortDate' },
      { item: 'lastUpdate', type: 'longDate' },
    ],
  );
};

const flattenData = (
  assessments: ICredentials[],
  lookups: CredentialingLookup[],
  onAttachmentAction: (
    action: AttachmentAction,
    reqID: number,
    requirementTypeID: number,
    travelerId: number,
    brandId: number,
  ) => void,
  level: number = 0,
  expandable: boolean = true,
  viewingDuplicates: boolean = false,
) => {
  let nestedItems = assessments;

  if (level === 0) {
    const grouped = groupedAssessments(assessments);
    nestedItems = nestedChildren(grouped);
  }
  if (level > 0) {
    nestedItems = childSorting(nestedItems, lookups);
  }
  const credentials: any[] = [];
  nestedItems?.forEach((item: ICredentials) => {
    const fieldLookups = getMetadataForReqID(lookups, item);

    const l: any = {
      rowID: item.requirementID,
      credentialName:
        level === 0 ? (
          getCredentialName(item.requirementType, item.credentialName)
        ) : (
          <VersionDetailsHoverView
            credential={[
              ...detailColumnsAndRows(item, fieldLookups, ['DateCompleted', 'ExpirationDate', 'LastUpdate'], true),
            ]}
            fieldLookups={fieldLookups}
            title={getCredentialName(item.requirementType, item.credentialName)}
            titleBold={getCredentialNameBold(item.requirementType, item.credentialName)}
          />
        ),
      credentialNameNoFormat: getCredentialNameNoFormat(item.requirementType, item.credentialName),
      dateCompleted: getDate(fieldLookups, 'DateCompleted', item.dateCompleted),
      dateCompletedNoFormat: getDateNoFormat(fieldLookups, 'DateCompleted', item.dateCompleted),
      lastUpdate: getDateText(fieldLookups, 'LastUpdate', item.lastUpdate),
      lastUpdateNoFormat: getDateNoFormat(fieldLookups, 'LastUpdate', item.lastUpdate),
      versioning: level === 0 ? <HistoryIcon /> : null,
      documentIcon: NoteIcon,
      moreIcon: <MoreVertIcon />,
      expandable: expandable,
      detailRows: [[...detailColumnsAndRows(item, fieldLookups, ['DateCompleted', 'ExpirationDate', 'LastUpdate'])]],
      documentIconHoverViewComponent: (
        <CredentialAttachmentToolTip
          onSelect={onAttachmentAction}
          requirementID={item.requirementID}
          requirementTypeID={item.requirementTypeID}
          travelerId={0}
          brandId={0}
        />
      ),
      versionRows:
        level === 0 && item['children'] !== undefined
          ? flattenData(item['children'], lookups, onAttachmentAction, level + 1, false, false)
          : null,
      versionColumns: getMainTableColumns(true, false, true),
    };
    credentials.push(l);
  });
  if (level === 0) {
    return credentials.sort(
      viewingDuplicates
        ? (a: any, b: any) => (a.lastUpdateNoFormat < b.lastUpdateNoFormat ? 1 : -1)
        : (a: any, b: any) => (a.dateCompletedNoFormat > b.dateCompletedNoFormat ? 1 : -1),
    );
  } else return credentials;
};

export const clinicalQualificationsAssessmentsData = {
  getTableColumns: getMainTableColumns,
  getTableRows: flattenData,
};
